import { Box, Drawer } from '@mui/material';
import isEmpty from 'lodash/isEmpty';
import { bool, object } from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Attachments from 'services/api/Attachments';
import Channel from 'services/api/Channel';
import authCompanyId from 'store/selectors/authCompanyId';
import CloseButton from '../CloseButton';
import connector from './connector';
import Form from './Form';
import excludeAppChannelMembers from './excludeAppChannelMembers';

const AcknowledgeCreateDrawer = ({ isOpen, actions, channel, currentEmployee = null }) => {
  const companyId = useSelector(authCompanyId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [channelMembers, setChannelMembers] = useState([]);

  const handleClose = () => {
    actions.drawers.acknowledgeCreate.close();
    // Clear temp attachment when closing
    actions.drawers.acknowledgeCreate.saveTemporarytempAttachment(null);
    setLoading(false);
    setError(false);
  };

  // Fetch channel members when drawer opens or channel changes
  const fetchChannelMembers = useCallback(async () => {
    if (!channel?._id || !isOpen) return;

    try {
      // For o2o (direct) channels, use currentEmployee and create member from channel
      if (channel.isDirect) {
        // For direct channels, channel._id is the user._id of the other person
        // Create an employee-like object from the channel
        const otherMember = {
          _id: channel._id,
          name: channel.name,
          avatar: channel.avatar,
          user: {
            _id: channel._id, // channel._id IS the other user's ID
          },
        };
        const members = [currentEmployee, otherMember].filter(Boolean);
        setChannelMembers(
          excludeAppChannelMembers(members, {
            isAppChannel: !!channel.isApp,
            channelId: channel._id,
          }),
        );
      } else {
        // For group channels, fetch members via API
        // Limit to 51 members to determine if oneForAll mode should be disabled (>50 members)
        // - onePerMember mode: Backend allows any group member to sign, signers array not sent
        // - oneForAll mode: Only available for groups with ≤50 members
        const res = await Channel.getMembers(channel._id, {
          skip: 0,
          limit: 51,
        });
        setChannelMembers(excludeAppChannelMembers(res.members || [], { isAppChannel: false }));
      }
    } catch (err) {
      console.error('Error fetching channel members:', err);
      setChannelMembers([]);
    }
  }, [
    channel?._id,
    channel?.isDirect,
    channel?.isApp,
    channel?.name,
    channel?.avatar,
    currentEmployee,
    isOpen,
  ]);

  useEffect(() => {
    fetchChannelMembers();
  }, [fetchChannelMembers]);

  const upload = async (file, uploadOptions = {}) => {
    try {
      const attachment = await Attachments.upload(
        companyId,
        channel._id,
        file,
        undefined,
        undefined,
        undefined,
        undefined,
        uploadOptions,
      );
      actions.drawers.acknowledgeCreate.saveTemporarytempAttachment(attachment);
    } catch (e) {
      console.log(e);
      setError(true);
    }
  };

  const onDrop = async (files, uploadOptions = {}) => {
    if (isEmpty(files)) return;
    setLoading(true);

    await upload(files[0], uploadOptions);

    setLoading(false);
  };

  const renderContent = () => {
    // Always show the form now, which includes upload section
    return (
      <Form
        onClose={handleClose}
        channelMembers={channelMembers}
        onDrop={onDrop}
        loading={loading}
        error={error}
      />
    );
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          justifyContent: 'center',
          width: 1100,
          maxWidth: 'calc(100vw - 40px)',
        }}
      >
        <Box sx={{ pt: '20px', pl: '20px' }}>
          <CloseButton onClick={handleClose} />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            padding: '20px',
            maxWidth: 'calc(100% - 60px)',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            position: 'relative',
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Drawer>
  );
};

AcknowledgeCreateDrawer.propTypes = {
  actions: object.isRequired,
  isOpen: bool.isRequired,
  channel: object.isRequired,
  tempAttachment: object,
  currentEmployee: object,
  authCompany: object,
};

export default connector(AcknowledgeCreateDrawer);
